import React, { forwardRef } from 'react';
import { Box } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterMoment } from '@mui/x-date-pickers-pro/AdapterMoment';
import moment from 'moment';
import { css } from 'aphrodite';
import { isDateValid } from '~/src/utils/validation';
import { getLocalDateFormat } from '~/src/utils/date';
import styles from './styles';

type DatePickerInputProps = {
  id?: string;
  label?: string;
  labelCssStyle?: object;
  value?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  noMargin?: boolean;
  dateFormat?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  onFocus?: () => void;
};

const DatePickerInput = forwardRef<HTMLInputElement, DatePickerInputProps>(
  (
    {
      id,
      label,
      labelCssStyle,
      value,
      hint,
      error,
      required,
      noMargin,
      dateFormat,
      onChange,
      onBlur,
      onFocus,
    },
    ref,
  ) => {
    const format = dateFormat || getLocalDateFormat();

    const parseValue = (
      dateString: string | undefined,
    ): moment.Moment | null => {
      if (!dateString) return null;

      // Try parsing as the specified format first
      const momentWithFormat = moment(dateString, format, true);
      if (momentWithFormat.isValid()) {
        return momentWithFormat;
      }

      // Try parsing as ISO format
      const momentISO = moment(dateString, moment.ISO_8601, true);
      if (momentISO.isValid()) {
        return momentISO;
      }

      // Fallback: try default moment parsing
      if (isDateValid(dateString)) {
        return moment(dateString);
      }

      return null;
    };

    const handleChange = (momentObj: moment.Moment | null) => {
      if (momentObj && momentObj.isValid()) {
        const formattedValue = momentObj.format(format);
        onChange?.(formattedValue);
      } else {
        onChange?.('');
      }
    };

    return (
      <Box className={css(noMargin ? styles.labelNoMargin : styles.label)}>
        {label && (
          <span className={css(styles.labelTitle, labelCssStyle)}>
            {label}
            {required && (
              <span className={css(styles.labelRequiredIcon)}> *</span>
            )}
          </span>
        )}
        {hint && <span className={css(styles.labelHint)}>{hint}</span>}
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <DatePicker
            slotProps={{
              textField: {
                id,
                fullWidth: true,
                size: 'small',
                placeholder: format,
                error: !!error,
                helperText: error,
                onBlur,
                onFocus,
                sx: {
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                  },
                },
              },
              field: { shouldRespectLeadingZeros: true },
            }}
            inputRef={ref}
            format={format}
            value={parseValue(value)}
            onChange={handleChange}
          />
        </LocalizationProvider>
      </Box>
    );
  },
);

DatePickerInput.displayName = 'DatePickerInput';

export default DatePickerInput;
